import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useChatbot } from 'contexts/ChatbotContext';
import { useTranslation } from 'react-i18next';
import 'components/chatbot/Chatbot.scss';
import 'highlight.js/styles/github-dark.css';
import LogoChatbot from 'assets/svg/logo-comcolors-chatbot.svg';

const ChatbotWindow: React.FC = () => {
  const { t } = useTranslation('translation');
  const {
    isOpen,
    isSidebarOpen,
    conversations,
    currentConversationId,
    closeChatbot,
    toggleSidebar,
    sendMessage,
    stopStreaming,
    unavailableReason,
  } = useChatbot();
  const [inputValue, setInputValue] = useState('');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);

  const currentConversation = conversations.find(
    (conv) => conv.id === currentConversationId,
  );

  const hasStreamingMessage = currentConversation?.messages.some(m => m.isStreaming);

  // Check if user is near bottom of messages
  const checkIfNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const threshold = 100; // pixels from bottom
    const position = container.scrollTop + container.clientHeight;
    const height = container.scrollHeight;

    return position >= height - threshold;
  };

  // Handle scroll to detect if user manually scrolled up
  const handleScroll = () => {
    setShouldAutoScroll(checkIfNearBottom());
  };

  // Scroll the messages container only. scrollIntoView() would also scroll
  // every ancestor, and on iOS that moves the page / visual viewport and
  // pushes this position:fixed window off the top of the screen.
  const scrollMessagesToBottom = (behavior: ScrollBehavior) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  };

  // Auto-scroll only if user is near bottom and not manually scrolling
  useEffect(() => {
    if (shouldAutoScroll) {
      // Use instant scroll during streaming to avoid shake
      scrollMessagesToBottom(hasStreamingMessage ? 'auto' : 'smooth');
    }
  }, [currentConversation?.messages, shouldAutoScroll, hasStreamingMessage]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isSidebarOpen) {
        closeChatbot();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isSidebarOpen, closeChatbot]);

  // iOS: when the keyboard opens, the layout viewport keeps its size, only the
  // visual viewport shrinks, and Safari pans the page to reveal the focused
  // textarea, which pushes this position:fixed window off the top. While the
  // keyboard is up, fit the window to the visible height and keep the page
  // pinned to the top; leave the page scroll alone otherwise.
  useEffect(() => {
    const viewport = window.visualViewport;
    const windowEl = windowRef.current;
    if (!isOpen || !viewport || !windowEl) return undefined;

    // Visible height with the keyboard closed, measured when the window opens
    const restingHeight = viewport.height;
    let keyboardOpen = false;

    const resetPosition = () => {
      windowEl.style.top = '';
      windowEl.style.height = '';
      windowEl.style.bottom = '';
    };

    const fitToKeyboard = () => {
      windowEl.style.top = '0px';
      windowEl.style.height = `${viewport.height}px`;
      windowEl.style.bottom = 'auto';
      // The textarea now sits above the keyboard: undo Safari's reveal pan
      window.scrollTo(0, 0);
    };

    const onResize = () => {
      const isMobileLayout = window.matchMedia('(max-width: 768px)').matches;
      const wasOpen = keyboardOpen;
      keyboardOpen = isMobileLayout && restingHeight - viewport.height > 100;
      if (keyboardOpen) {
        fitToKeyboard();
        scrollMessagesToBottom('auto');
      } else {
        resetPosition();
        if (wasOpen) window.scrollTo(0, 0);
      }
    };

    // Safari may pan the page after the resize: keep it pinned while typing
    const onScroll = () => {
      if (keyboardOpen) fitToKeyboard();
    };

    viewport.addEventListener('resize', onResize);
    viewport.addEventListener('scroll', onScroll);

    return () => {
      viewport.removeEventListener('resize', onResize);
      viewport.removeEventListener('scroll', onScroll);
      resetPosition();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue('');
      setShouldAutoScroll(true); // Always auto-scroll when sending a new message
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter, new line on Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim()) {
        sendMessage(inputValue);
        setInputValue('');
        setShouldAutoScroll(true);
      }
    }
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  return (
    <div className="chatbot-window" ref={windowRef}>
      <div className="chatbot-window-header">
        <button
          className="chatbot-window-menu-btn"
          onClick={toggleSidebar}
          aria-label={t('chatbot.openconversations')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M2 4H18M2 10H18M2 16H18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className='chatbot-window-title'>
          <img src={LogoChatbot} alt="ComColors Chatbot" className="chatbot-button-logo" />
          <h2>{t('chatbot.assistant')}</h2>
        </div>
        <button
          className="chatbot-window-close-btn"
          onClick={closeChatbot}
          aria-label={t('chatbot.closechatbot')}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="chatbot-window-messages" ref={messagesContainerRef} onScroll={handleScroll}>
        {currentConversation?.isLoadingHistory ? (
          <div className="chatbot-window-loading">
            <div className="chatbot-loading-spinner">
              <div className="chatbot-loading-dot"></div>
              <div className="chatbot-loading-dot"></div>
              <div className="chatbot-loading-dot"></div>
            </div>
          </div>
        ) : !currentConversation || currentConversation.messages.length === 0 ? (
          <div className="chatbot-window-empty">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
              <circle cx="16" cy="22" r="2" fill="currentColor" />
              <circle cx="24" cy="22" r="2" fill="currentColor" />
              <circle cx="32" cy="22" r="2" fill="currentColor" />
              <path
                d="M14 30C14 30 17 34 24 34C31 34 34 30 34 30"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <p>{t('chatbot.startconversation')}</p>
            <span>{t('chatbot.askmeanything')}</span>
          </div>
        ) : (
          <>
            {currentConversation.messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot-message ${message.sender}${message.isError ? ' error' : ''}`}
              >
                <div className="chatbot-message-content">
                  <div className="chatbot-message-text">
                    {message.isError ? (
                      // "Coach unavailable" notice: plain text, the backend error is not markdown
                      <p>{message.text}</p>
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeHighlight]}
                      >
                        {message.text}
                      </ReactMarkdown>
                    )}
                    {message.isStreaming && <span className="chatbot-message-cursor"></span>}
                  </div>
                  <span className="chatbot-message-time">
                    {formatTime(message.timestamp)}
                  </span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {unavailableReason && (
        <div className="chatbot-window-unavailable" role="alert">
          {t('chatbot.unavailable', { reason: unavailableReason })}
        </div>
      )}

      <form className="chatbot-window-input" onSubmit={handleSubmit}>
        <textarea
          placeholder={t('chatbot.typemessage')}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          // Keyboard closed: undo the pan iOS applied to reveal the field
          onBlur={() => window.setTimeout(() => window.scrollTo(0, 0), 50)}
          aria-label={t('chatbot.messageinput')}
          rows={1}
        />
        <button
          type={hasStreamingMessage ? 'button' : 'submit'}
          disabled={!hasStreamingMessage && !inputValue.trim()}
          onClick={hasStreamingMessage ? stopStreaming : undefined}
          aria-label={hasStreamingMessage ? t('chatbot.stopstreaming') : t('chatbot.sendmessage')}
          className={hasStreamingMessage ? 'chatbot-stop-button' : ''}
        >
          {hasStreamingMessage ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="3" y="3" width="10" height="10" rx="2" fill="currentColor" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M2 10L18 2L10 18L8 11L2 10Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
};

export default ChatbotWindow;
